import React, { useCallback, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Typography from '@quintoandar/cozy-core/Typography';
import Box from '@quintoandar/cozy-core/Box';
import { deletePhotosAction } from 'containers/PhotosManager/action';
import UploadButton from 'components/UploadImage';
import { LoadingButton } from 'components/LoadingButton';
import { createStructuredSelector } from 'reselect';
import { makeSelectPhotoListDropzoneVisibility } from 'containers/PhotosManager/selectors';
import DropMultiUploader from 'components/UploadImage/DropMultiUploader';
import PhotosManagerCardsWrapper from './PhotosManagerCardsWrapper';
import { BoxIndex, DropZoneWrapper, HeaderWrapper } from './elements';
import messages from './messages';

export const PhotosManagerContent = ({
  galleryType,
  onSortEnd,
  photos,
  deletePhoto,
  dropzoneVisibility,
  houseId,
  houseType,
  propertyId,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [deletingAllPhotospheres, setDeletingAllPhotospheres] = useState(false);

  const handleDeleteAll = useCallback(photoList => () => {
    setDeletingAllPhotospheres(true);

    photoList.forEach(photo => {
      deletePhoto(photo.id, galleryType, houseId);
    });
  });

  const handleSortStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleSortEnd = useCallback((data) => {
    onSortEnd(data);
    window.requestAnimationFrame(() => setIsDragging(false));
  }, [photos]);

  return (
    <BoxIndex mt={4}>
      <HeaderWrapper
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        flexWrap="wrap"
      >
        <Box my={1}>
          <Typography>
            {photos.length}{' '}
            {galleryType === 'photosphere' ? (
              <FormattedMessage
                values={{ photos: photos.length }}
                {...messages.title}
              />
            ) : (
              <FormattedMessage
                values={{ photos: photos.length }}
                {...messages.photoTitle}
              />
            )}
          </Typography>
        </Box>
        <UploadButton />
      </HeaderWrapper>
      <DropZoneWrapper
        showContent={dropzoneVisibility}
        mt={dropzoneVisibility ? 3 : 0}
      >
        <DropMultiUploader
          houseId={houseId}
          houseType={houseType}
          galleryType={galleryType}
        />
      </DropZoneWrapper>
      {/* Because of SortableContainer it was necessary to keep the wrapper component */}
      <PhotosManagerCardsWrapper
        houseId={houseId}
        galleryType={galleryType}
        onSortStart={handleSortStart}
        onSortEnd={handleSortEnd}
        photos={photos}
        propertyId={propertyId}
        distance={1}
        axis="xy"
        isDragging={isDragging}
      />
      <Box mt={6}>
        <LoadingButton
          isLoading={deletingAllPhotospheres}
          onClickFunction={handleDeleteAll(photos)}
        />
      </Box>
    </BoxIndex>
  );
};

const mapDispatchToProps = dispatch => ({
  deletePhoto: (id, galleryType, houseId) =>
    dispatch(deletePhotosAction(id, galleryType, houseId)),
});

const mapStateToProps = createStructuredSelector({
  dropzoneVisibility: makeSelectPhotoListDropzoneVisibility(),
});

PhotosManagerContent.propTypes = {
  galleryType: PropTypes.string,
  onSortEnd: PropTypes.func,
  photos: PropTypes.array,
  deletePhoto: PropTypes.func,
  dropzoneVisibility: PropTypes.bool,
  houseId: PropTypes.string,
  houseType: PropTypes.string,
  propertyId: PropTypes.string,
};

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(withConnect)(PhotosManagerContent);
